import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Seo from '@/components/Seo';
import { Reveal, Magnetic } from '@/components/motion';
import { ServiceGlyph } from '@/components/diagrams';
import { CtaSection } from '@/components/PageHero';
import { SERVICES } from '@/content/site';

const START_OPTIONS = [
  { label: 'Healthcare billing and collections', to: '/solutions/healthcare-revenue' },
  { label: 'Employee benefits', to: '/solutions/employee-benefits' },
  { label: 'Technology expenses', to: '/solutions/technology-costs' },
  { label: 'Shipping expenses', to: '/solutions/shipping-costs' },
  { label: 'Marketing and growth', to: '/solutions/ai-marketing' },
  { label: "I'm not sure", to: '/contact?interest=not-sure' },
];

function ServiceCard({ s, className = '', featured = false }) {
  return (
    <Link
      to={s.path}
      data-testid={`solutions-card-${s.slug}`}
      className={`glass glass-hover relative overflow-hidden group block ${className}`}
    >
      {featured && (
        <>
          <img
            src={s.image}
            alt={s.imageAlt}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 group-hover:scale-105 transition-all duration-700"
            style={{ filter: 'saturate(0.75) brightness(0.8)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(7,29,24,0.55), rgba(7,29,24,0.92))' }} />
        </>
      )}
      <div className={`relative p-8 lg:p-10 ${featured ? 'flex flex-col justify-end min-h-[420px] lg:min-h-[520px] h-full' : ''}`}>
        <div className="flex items-start justify-between gap-6">
          <ServiceGlyph name={s.slug} className="w-14 h-14 flex-none" />
          <ArrowUpRight
            size={22}
            className="text-goldbright opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
          />
        </div>
        <div className={featured ? 'mt-auto pt-10' : 'mt-16'}>
          <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-goldbright">{s.num}</p>
          <h3 className="h3 mt-3">{s.title}</h3>
          <p className="mt-3 text-[14.5px] leading-relaxed text-mist max-w-[52ch]">{s.short}</p>
        </div>
      </div>
    </Link>
  );
}

export default function Solutions() {
  const byslug = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));
  return (
    <>
      <Seo
        title="Business Solutions Overview | KTK Advisors"
        description="Five practice areas that shape your bottom line: healthcare revenue, employee benefits, technology costs, shipping spend, and AI-supported marketing growth."
        path="/solutions"
      />

      <section className="relative overflow-hidden bg-deep" data-testid="solutions-hero">
        <div className="aura aura--emerald" style={{ width: 640, height: 640, top: -260, left: -160 }} />
        <div className="wrap pt-40 pb-20 lg:pt-48 lg:pb-24 relative">
          <Reveal>
            <p className="eyebrow-mono">Solutions Overview</p>
            <h1 className="h1 mt-7 max-w-[24ch]">
              Practical opportunities across the areas that shape your{' '}
              <span className="gold-italic">bottom line.</span>
            </h1>
            <p className="lede mt-8 max-w-[58ch]">
              KTK Advisors evaluates five service areas—and coordinates suitable
              specialized support where it strengthens your result. Start with the
              area closest to your challenge.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep pb-4 section" data-testid="solutions-grid">
        <div className="wrap">
          <Reveal>
            <div className="grid gap-5 lg:grid-cols-3">
              <ServiceCard s={byslug['healthcare-revenue']} featured className="lg:col-span-2 lg:row-span-2" />
              <ServiceCard s={byslug['employee-benefits']} />
              <ServiceCard s={byslug['technology-costs']} />
              <ServiceCard s={byslug['shipping-costs']} className="lg:col-span-2" />
              <ServiceCard s={byslug['ai-marketing']} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-deep section" data-testid="start-selector">
        <div className="wrap grid lg:grid-cols-[1fr_1.4fr] gap-12 items-start">
          <Reveal>
            <p className="eyebrow-mono">Where should we start?</p>
            <h2 className="h2 mt-6 max-w-[18ch]">Pick the area closest to your challenge.</h2>
            <p className="body-copy text-mist mt-6 max-w-[46ch]">
              You'll land on the service page to explore it in plain terms—or,
              if you're not sure yet, straight to a short consultation.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass p-3">
              {START_OPTIONS.map((o, i) => (
                <Link
                  key={o.label}
                  to={o.to}
                  data-testid={`start-option-${i + 1}`}
                  className="group flex items-center justify-between gap-6 px-6 py-5 rounded-xl hover:bg-cardhi transition-colors duration-300"
                >
                  <span className="flex items-center gap-5">
                    <span className="font-mono text-[11px] text-goldbright">0{i + 1}</span>
                    <span className="font-semibold text-[15.5px] text-ivory">{o.label}</span>
                  </span>
                  <ArrowRight
                    size={19}
                    className="text-goldbright group-hover:translate-x-1.5 transition-transform duration-300"
                  />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        title="Not sure which area fits? That's a fine place to start."
        copy="Describe the challenge in your own words. We'll help you figure out where the opportunity is—and whether it's worth pursuing."
        buttonLabel="Schedule a Consultation"
        buttonTo="/contact"
        testid="solutions-cta-button"
      />
    </>
  );
}
